import React, { useState, useEffect } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { 
  CreditCard, 
  Check,
  Crown,
  Calendar,
  CurrencyCircleDollar,
  Wallet,
  ArrowsClockwise,
  Warning,
  CurrencyBtc
} from "phosphor-react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import GeniLoader from "../components/GeniLoader";

// Real Stripe prices in BRL (must match backend STRIPE_PRICES_BRL)
const STRIPE_PRICES = {
  monthly: { amount: 49.90, currency: "BRL", formatted: "R$49,90" },
  yearly: { amount: 442.00, currency: "BRL", formatted: "R$442,00" }
};

// Crypto prices in USD
const CRYPTO_PRICES = {
  monthly: { amount: 10.00, currency: "USD", formatted: "$10.00" },
  yearly: { amount: 89.00, currency: "USD", formatted: "$89.00" },
  lifetime: { amount: 394.00, currency: "USD", formatted: "$394.00" }
};

export default function Billing() {
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await api.get("/user/profile");
      setProfile(response.data);
    } catch (error) {
      console.error("Error fetching profile:", error);
      toast.error("Failed to load subscription data");
    } finally {
      setLoading(false);
    }
  };

  // Determine billing provider based on profile fields
  const getBillingProvider = () => {
    if (!profile) return null;
    
    // Check for Stripe subscription evidence
    if (profile.stripe_customer_id && profile.stripe_subscription_id) {
      return 'stripe';
    }
    
    // Check for crypto subscription evidence
    if (profile.crypto_subscription_active) {
      return 'crypto';
    }
    
    // Fallback: if PRO but no clear provider, assume manual/crypto
    if (profile.subscription_status === 'pro') {
      return 'manual';
    }
    
    return null;
  };

  // Get subscription details based on provider and plan
  const getSubscriptionDetails = () => {
    if (!profile || profile.subscription_status !== 'pro') {
      return null;
    }

    const provider = getBillingProvider();
    const plan = profile.subscription_plan || 'monthly';
    const stripeStatus = profile.stripe_subscription_status;
    const cancelAtPeriodEnd = profile.stripe_cancel_at_period_end;

    // Determine display status
    let displayStatus = 'active';
    let statusColor = 'text-[#00E676]';
    let statusBgColor = 'bg-[#00E676]';
    
    if (provider === 'stripe') {
      switch (stripeStatus) {
        case 'active':
          if (cancelAtPeriodEnd) {
            displayStatus = 'Cancels at period end';
            statusColor = 'text-[#FFA726]';
            statusBgColor = 'bg-[#FFA726]';
          } else {
            displayStatus = 'Active';
          }
          break;
        case 'past_due':
          displayStatus = 'Payment issue';
          statusColor = 'text-[#FFA726]';
          statusBgColor = 'bg-[#FFA726]';
          break;
        case 'unpaid':
          displayStatus = 'Inactive';
          statusColor = 'text-[#FF3B30]';
          statusBgColor = 'bg-[#FF3B30]';
          break;
        case 'canceled':
          displayStatus = 'Canceled';
          statusColor = 'text-[#FF3B30]';
          statusBgColor = 'bg-[#FF3B30]';
          break;
        default:
          displayStatus = 'Active';
      }
    } else {
      // Crypto/manual - just check expiration
      const expiresAt = profile.subscription_expires_at ? new Date(profile.subscription_expires_at) : null;
      if (expiresAt && expiresAt < new Date()) {
        displayStatus = 'Expired';
        statusColor = 'text-[#FF3B30]';
        statusBgColor = 'bg-[#FF3B30]';
      }
    }

    // Get amount and currency based on provider
    let amount, currency, formattedAmount;
    if (provider === 'stripe') {
      const prices = STRIPE_PRICES[plan] || STRIPE_PRICES.monthly;
      amount = prices.amount;
      currency = prices.currency;
      formattedAmount = prices.formatted;
    } else {
      const prices = CRYPTO_PRICES[plan] || CRYPTO_PRICES.monthly;
      amount = prices.amount;
      currency = prices.currency;
      formattedAmount = prices.formatted;
    }

    // Get payment method display
    let paymentMethod, paymentIcon;
    if (provider === 'stripe') {
      paymentMethod = 'Credit Card (Stripe)';
      paymentIcon = <CreditCard size={18} className="text-[#635BFF]" />;
    } else {
      paymentMethod = 'Crypto (USDT)';
      paymentIcon = <CurrencyBtc size={18} className="text-[#F0B90B]" />;
    }

    // Get date label and value
    let dateLabel, dateValue;
    const expiresAt = profile.subscription_expires_at ? new Date(profile.subscription_expires_at) : null;
    
    if (provider === 'stripe') {
      if (cancelAtPeriodEnd || stripeStatus === 'canceled') {
        dateLabel = 'Access Until';
      } else if (stripeStatus === 'active' || stripeStatus === 'past_due') {
        dateLabel = 'Next Billing Date';
      } else {
        dateLabel = 'Period Ends';
      }
    } else if (plan === 'lifetime') {
      dateLabel = 'Access';
    } else {
      dateLabel = 'Expiration Date';
    }
    dateValue = expiresAt;

    return {
      plan,
      provider,
      displayStatus,
      statusColor,
      statusBgColor,
      amount,
      currency,
      formattedAmount,
      paymentMethod,
      paymentIcon,
      dateLabel,
      dateValue,
      cancelAtPeriodEnd,
      stripeStatus,
      hasStripeCustomer: !!profile.stripe_customer_id
    };
  };

  // Open Stripe Customer Portal
  const openCustomerPortal = async () => {
    setPortalLoading(true);
    try {
      const response = await api.post('/payments/stripe/create-portal-session');
      
      if (response.data.portal_url) {
        window.location.href = response.data.portal_url;
      } else {
        toast.error('Could not create portal session');
      }
    } catch (error) {
      console.error('Error creating portal session:', error);
      toast.error(error.response?.data?.detail || 'Failed to open billing portal');
    } finally {
      setPortalLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <GeniLoader size="md" />
      </div>
    );
  }

  const subscription = getSubscriptionDetails();
  const isStripeSubscription = subscription?.provider === 'stripe';

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="billing-page">
      <div className="w-full max-w-2xl mx-auto px-4 md:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-black font-heading tracking-tight flex items-center gap-3">
            <CreditCard size={32} weight="duotone" className="text-white/60" />
            Billing
          </h1>
          <p className="text-white/60 mt-2">
            View your subscription details.
          </p>
        </div>

        {/* No Subscription */}
        {!subscription && (
          <div className="bg-[#121212] border border-white/10 rounded-sm p-8 text-center" data-testid="no-subscription">
            <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
              <Crown size={32} className="text-white/30" />
            </div>
            <h2 className="text-xl font-bold mb-2">No Active Subscription</h2>
            <p className="text-white/50 text-sm">
              Upgrade to Pro to unlock unlimited trades and advanced analytics.
            </p>
          </div>
        )}

        {/* Subscription Card */}
        {subscription && (
          <div className="bg-[#121212] border border-white/10 rounded-sm overflow-hidden" data-testid="subscription-card">
            {/* Card Header */}
            <div className={`px-6 py-4 border-b border-white/10 ${
              isStripeSubscription 
                ? 'bg-gradient-to-r from-[#635BFF]/10 to-transparent' 
                : 'bg-gradient-to-r from-[#F0B90B]/10 to-transparent'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 ${
                    isStripeSubscription ? 'bg-[#635BFF]/20' : 'bg-[#F0B90B]/20'
                  } rounded-sm flex items-center justify-center`}>
                    <Crown size={22} weight="fill" className={
                      isStripeSubscription ? 'text-[#635BFF]' : 'text-[#F0B90B]'
                    } />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold">Subscription</h2>
                    <p className="text-white/40 text-xs">
                      {isStripeSubscription ? 'Stripe' : 'Crypto'} billing
                    </p>
                  </div>
                </div>
                
                {/* Manage Subscription Button - Stripe only */}
                {isStripeSubscription && subscription.hasStripeCustomer && (
                  <Button
                    onClick={openCustomerPortal}
                    disabled={portalLoading}
                    variant="outline"
                    className="bg-transparent border border-[#635BFF]/50 hover:bg-[#635BFF]/10 text-[#635BFF] text-sm px-4 py-2"
                    data-testid="manage-subscription-btn"
                  >
                    {portalLoading ? (
                      <ArrowsClockwise className="animate-spin mr-2" size={16} />
                    ) : (
                      <CreditCard weight="fill" className="mr-2" size={16} />
                    )}
                    Manage Subscription
                  </Button>
                )}
              </div>
            </div>

            {/* Card Content */}
            <div className="p-6 space-y-4">
              {/* Plan */}
              <div className="flex items-center justify-between py-3 border-b border-white/5" data-testid="billing-plan">
                <div className="flex items-center gap-3">
                  <Crown size={18} className="text-white/40" />
                  <span className="text-white/60">Plan</span>
                </div>
                <span className={`font-bold ${isStripeSubscription ? 'text-[#635BFF]' : 'text-[#F0B90B]'}`}>
                  Pro {{ monthly: 'Monthly', yearly: 'Yearly', lifetime: 'Lifetime' }[subscription.plan] || 'Monthly'}
                </span>
              </div>

              {/* Status */}
              <div className="flex items-center justify-between py-3 border-b border-white/5" data-testid="billing-status">
                <div className="flex items-center gap-3">
                  {subscription.stripeStatus === 'past_due' ? (
                    <Warning size={18} className="text-[#FFA726]" />
                  ) : (
                    <Check size={18} className="text-white/40" />
                  )}
                  <span className="text-white/60">Status</span>
                </div>
                <span className={`font-bold flex items-center gap-2 ${subscription.statusColor}`}>
                  <span className={`w-2 h-2 rounded-full ${subscription.statusBgColor}`} />
                  {subscription.displayStatus}
                </span>
              </div>

              {/* Amount */}
              <div className="flex items-center justify-between py-3 border-b border-white/5" data-testid="billing-amount">
                <div className="flex items-center gap-3">
                  <CurrencyCircleDollar size={18} className="text-white/40" />
                  <span className="text-white/60">
                    {isStripeSubscription ? 'Price' : 'Amount Paid'}
                  </span>
                </div>
                <span className="font-mono font-bold text-white">
                  {subscription.formattedAmount}
                  <span className="text-white/40 text-sm ml-1">
                    {subscription.plan === 'lifetime' ? ' one-time' : `/${subscription.plan === 'yearly' ? 'year' : 'month'}`}
                  </span>
                </span>
              </div>

              {/* Date */}
              <div className="flex items-center justify-between py-3 border-b border-white/5" data-testid="billing-date">
                <div className="flex items-center gap-3">
                  <Calendar size={18} className="text-white/40" />
                  <span className="text-white/60">{subscription.dateLabel}</span>
                </div>
                <span className="font-mono text-white">
                  {subscription.dateValue 
                    ? format(subscription.dateValue, "MMM d, yyyy")
                    : subscription.plan === 'lifetime' ? 'No expiration' : 'N/A'
                  }
                </span>
              </div>

              {/* Payment Method */}
              <div className="flex items-center justify-between py-3" data-testid="billing-payment-method">
                <div className="flex items-center gap-3">
                  <Wallet size={18} className="text-white/40" />
                  <span className="text-white/60">Payment Method</span>
                </div>
                <span className="text-white/80 flex items-center gap-2">
                  {subscription.paymentIcon}
                  {subscription.paymentMethod}
                </span>
              </div>
            </div>

            {/* Cancellation Notice */}
            {subscription.cancelAtPeriodEnd && (
              <div className="px-6 py-4 bg-[#FFA726]/10 border-t border-[#FFA726]/20" data-testid="cancellation-notice">
                <p className="text-[#FFA726] text-sm flex items-center gap-2">
                  <Warning size={16} />
                  Your subscription will not renew. Access continues until {
                    subscription.dateValue ? format(subscription.dateValue, "MMM d, yyyy") : 'the end of your billing period'
                  }.
                </p>
              </div>
            )}

            {/* Past Due Notice */}
            {subscription.stripeStatus === 'past_due' && (
              <div className="px-6 py-4 bg-[#FFA726]/10 border-t border-[#FFA726]/20" data-testid="past-due-notice">
                <p className="text-[#FFA726] text-sm flex items-center gap-2">
                  <Warning size={16} />
                  Payment failed. Please update your payment method to avoid service interruption.
                </p>
                {subscription.hasStripeCustomer && (
                  <Button
                    onClick={openCustomerPortal}
                    disabled={portalLoading}
                    variant="outline"
                    size="sm"
                    className="mt-3 bg-transparent border border-[#FFA726]/50 hover:bg-[#FFA726]/10 text-[#FFA726] text-xs"
                    data-testid="update-payment-btn"
                  >
                    Update Payment Method
                  </Button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Info Note */}
        <div className="mt-6 p-4 bg-[#0A0A0A] border border-white/10 rounded-sm">
          <p className="text-white/40 text-xs text-center">
            {isStripeSubscription 
              ? 'Use "Manage Subscription" to update payment method, change plan, or cancel.'
              : 'For billing questions or to renew your subscription, please contact support.'
            }
          </p>
        </div>
      </div>
    </div>
  );
}
